import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { upNextMeta } from './continueModel';
import type { TutorialLesson } from './tutorialsModel';

interface UpNextProps {
  next: TutorialLesson | null;
  onOpen: (id: string) => void;
  onClose: () => void;
  className?: string;
}

/** A small 16:9 picture of the next lesson: its first screenshot, or its icon on a quiet tile. */
function Thumb({ lesson }: { lesson: TutorialLesson }) {
  return (
    <span className="relative flex aspect-video w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-secondary text-subtle">
      {lesson.thumbnail ? <img src={lesson.thumbnail} alt="" loading="lazy" className="size-full object-cover object-top" /> : <Icon name={lesson.icon} size={20} />}
    </span>
  );
}

/**
 * "Up next", visible for the whole lesson: the next lesson's title and length, one tap to jump to it (it plays). On the
 * last lesson it says so and offers the way back to the list.
 */
export function UpNext({ next, onOpen, onClose, className }: UpNextProps) {
  return (
    <section aria-label="Up next" data-testid="lesson-up-next" className={cn('flex min-w-0 flex-col rounded-lg border bg-card shadow-card', className)}>
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <span className="text-sm font-semibold text-foreground">Up next</span>
      </div>
      {next ? (
        <button
          type="button"
          data-testid="lesson-up-next-open"
          data-lesson={next.id}
          onClick={() => onOpen(next.id)}
          className="lift-row m-1.5 flex min-h-11 items-center gap-3 rounded-md p-2 text-left outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <Thumb lesson={next} />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="line-clamp-2 text-[13px] font-semibold leading-snug text-foreground">{next.title}</span>
            <span className="tabular text-xs text-muted-foreground">{upNextMeta(next)}</span>
          </span>
          <Icon name="play_circle" size={22} className="shrink-0 text-muted-foreground" />
        </button>
      ) : (
        <div data-testid="lesson-up-next-last" className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <span className="text-[13px] text-muted-foreground">{upNextMeta(null)}</span>
          <Button variant="outline" size="sm" onClick={onClose}>
            All tutorials
          </Button>
        </div>
      )}
    </section>
  );
}
